import { Link, useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { TwoFactorChallenge } from '@nucleus-commerce/api-client/platform';
import { Button } from '@nucleus-commerce/ui/components/button';

import { ServerError } from '@/shared/ui/server-error';

import { useGoogleCallback } from '../hooks/use-google-callback';
import { googleFailure } from '../schemas/google-rules';
import { isTwoFactorChallenge, type ChallengeEnd } from '../schemas/two-factor-rules';

import { AuthCard } from './auth-frame';
import { DevGoogleAccountForm } from './dev-google-account-form';
import { TwoFactorStep } from './two-factor-step';

export interface GoogleCallbackCardProps {
  code: string | undefined;
  state: string | undefined;
  /** Google sends `error` (e.g. access_denied) instead of a code when the person backs out. */
  error: string | undefined;
  /** `dev=1`: the backend's development driver, which leaves picking the account to this page. */
  dev?: boolean | undefined;
}

/**
 * Exchanges Google's single-use code once per page (the ref survives StrictMode's second effect
 * run). A 202 continues with the same code step as the password sign-in.
 */
export function GoogleCallbackCard({
  code: googleCode,
  state,
  error,
  dev,
}: GoogleCallbackCardProps) {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const exchange = useGoogleCallback();
  const { mutate } = exchange;
  const started = useRef<string | null>(null);
  const [challengeEnd, setChallengeEnd] = useState<ChallengeEnd | null>(null);
  const [devCode, setDevCode] = useState<string | null>(null);
  const code = googleCode ?? devCode ?? undefined;
  const result = exchange.data;
  const challenge: TwoFactorChallenge | null =
    result && isTwoFactorChallenge(result) && !challengeEnd ? result : null;
  const signedIn = result !== undefined && !isTwoFactorChallenge(result);

  const enter = useCallback(() => navigate({ to: '/', replace: true }), [navigate]);
  const toSignIn = useCallback(() => navigate({ to: '/login' }), [navigate]);
  const onExpired = useCallback((reason: ChallengeEnd) => setChallengeEnd(reason), []);

  useEffect(() => {
    if (error || !code || !state || started.current === code) return;
    started.current = code;
    mutate({ code, state });
  }, [code, state, error, mutate]);

  useEffect(() => {
    if (signedIn) void enter();
  }, [signedIn, enter]);

  const startAgain = (
    <Button asChild className="w-full">
      <Link to="/login">{t('google.tryAgain')}</Link>
    </Button>
  );

  if (dev && !code && state && !error) {
    return (
      <AuthCard id="google-callback" title={t('google.dev.title')}>
        <DevGoogleAccountForm onPick={setDevCode} />
      </AuthCard>
    );
  }

  if (error || !code || !state) {
    return (
      <AuthCard id="google-callback" title={t('google.cancelledTitle')}>
        <div className="flex flex-col gap-4">
          <p className="text-muted-foreground text-sm">
            {error ? t('google.cancelled') : t('google.missing')}
          </p>
          {startAgain}
        </div>
      </AuthCard>
    );
  }

  if (challenge) {
    return (
      <AuthCard id="google-callback" title={t('twoFactor.title')}>
        <TwoFactorStep
          challenge={challenge}
          onVerified={enter}
          onBack={() => void toSignIn()}
          onExpired={onExpired}
          backLabel={t('frame.backToSignIn')}
        />
      </AuthCard>
    );
  }

  if (challengeEnd) {
    return (
      <AuthCard id="google-callback" title={t(`twoFactor.${challengeEnd}Title`)}>
        <div className="flex flex-col gap-4">
          <p role="alert" className="text-muted-foreground text-sm">
            {t('google.challengeEnded')}
          </p>
          {startAgain}
        </div>
      </AuthCard>
    );
  }

  if (exchange.isError) {
    const failure = googleFailure(exchange.error);
    return (
      <AuthCard id="google-callback" title={t(`google.failure.${failure}Title`)}>
        <div className="flex flex-col gap-4">
          {failure === 'failed' ? (
            <ServerError error={exchange.error} />
          ) : (
            <p role="alert" className="text-muted-foreground text-sm">
              {t(`google.failure.${failure}`)}
            </p>
          )}
          {startAgain}
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard id="google-callback" title={t('google.signingInTitle')}>
      <p role="status" className="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2 className="size-4 animate-spin" aria-hidden />
        {t('google.signingIn')}
      </p>
    </AuthCard>
  );
}
